// Sprint 5.6 (D9) — Subagent (Agent / Task) collapsible sub-chat panel.
// Sprint ai-elements-rendering (D-026) — rebuilt as a RECURSIVE tree on
// the AI Elements ChainOfThought primitives.
//
// When the parent agent calls Anthropic's `Task` tool (or any future
// `Agent` variant), the spawned sub-agent emits its own assistant_text
// / tool_use / tool_result events. Anthropic stamps each one with
// `parent_tool_use_id` pointing back to the parent's tool_use id — and
// since Claude Code v2.1.172 subagents can spawn their own subagents up
// to 5 levels deep. The panel receives the full `childrenByParent` map
// from `groupBySubagent()` and recurses: a child that is itself a
// Task/Agent tool_use renders as a nested SubagentPanel at depth+1.
//
// Header convention (all pre-existing segments preserved — spec §3.2):
//   [Nk] Subagent · <subagent_type> · <model> · N tools (+M) · X.Xs
// where the depth badge (N1, N2, …) and the `(+M)` descendant-tool count
// render ONLY when the tree actually nests — a depth-1-only history
// renders identically to the flat pre-sprint panel (spec §6.7
// backward-compat gate).
//
// C11 — when a subagent errors (final tool_result has is_error) the
// header turns rose. D-msg-12 — raw prompt + final result are
// developer-only. D-msg-16 — the tool-count segment is suppressed at 0.
//
// Indent strategy for 375px viewports (spec §7): each level indents by
// pl-3 (12px) + border-l only — 5 levels cost 60px, leaving ~300px of
// usable width. No per-level padding multiplication.

import { useMemo } from 'react';
import { BotIcon } from 'lucide-react';
import type { Message } from '../types';
import {
  asAssistantText,
  asToolResult,
  asToolUse,
  pairToolResults,
  toolResultText,
  SUBAGENT_TOOL_NAMES,
} from '../lib/messages';
import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
} from './ai-elements/chain-of-thought';
import { Badge } from '@/components/ui/badge';
import { MessageBubble } from './MessageBubble';
import { useTranslation } from '../i18n';
import { DeveloperOnly } from './founder/DeveloperOnly';
import { modelLabel } from '../lib/models';

// Hard ceiling on render recursion. The runtime supports 5 levels; the
// ceiling exists so malformed histories (a parent_tool_use_id cycle)
// degrade to flat ToolCallRow rendering instead of a stack overflow.
const MAX_RENDER_DEPTH = 8;

export interface SubagentPanelProps {
  // The parent tool_use message (the Agent/Task call itself).
  toolUseMessage: Message;
  // Full parent_tool_use_id → children map from groupBySubagent(). The
  // panel looks up its own immediate children and hands the same map to
  // nested panels.
  childrenByParent: Map<string, Message[]>;
  // The matching tool_result (the subagent's final response), if landed.
  resultMessage?: Message;
  // 1-based nesting level. ConversationList always mounts at 1.
  depth?: number;
}

// Count every tool_use in the subtree below `parentId`, descending into
// nested subagents. Used for the `(+M)` descendant segment.
function countSubtreeTools(
  parentId: string,
  childrenByParent: Map<string, Message[]>,
  depthGuard = 0,
): number {
  if (depthGuard > MAX_RENDER_DEPTH) return 0;
  let n = 0;
  for (const m of childrenByParent.get(parentId) ?? []) {
    if (m.type !== 'tool_use') continue;
    n += 1;
    const tu = asToolUse(m);
    if (SUBAGENT_TOOL_NAMES.has(tu.name) && tu.tool_use_id) {
      n += countSubtreeTools(tu.tool_use_id, childrenByParent, depthGuard + 1);
    }
  }
  return n;
}

export function SubagentPanel({
  toolUseMessage,
  childrenByParent,
  resultMessage,
  depth = 1,
}: SubagentPanelProps) {
  const { t } = useTranslation();

  const tu = asToolUse(toolUseMessage);
  const tr = resultMessage ? asToolResult(resultMessage) : null;

  const childMessages = useMemo(
    () =>
      (tu.tool_use_id ? childrenByParent.get(tu.tool_use_id) : undefined) ?? [],
    [childrenByParent, tu.tool_use_id],
  );

  // Subagent prompt + type live in the input. Anthropic exposes
  // `subagent_type` (e.g. "explore", "shell", "generalPurpose") and
  // `prompt` (the instructions for the subagent).
  const subagentType =
    typeof tu.input.subagent_type === 'string'
      ? tu.input.subagent_type
      : typeof tu.input.description === 'string'
        ? tu.input.description
        : 'subagent';
  const subagentPrompt =
    typeof tu.input.prompt === 'string' ? tu.input.prompt : '';

  // Count tool calls inside the subagent for the header summary.
  const toolCount = useMemo(
    () => childMessages.filter((m) => m.type === 'tool_use').length,
    [childMessages],
  );

  // Tools run by NESTED subagents (subtree minus immediate). Zero for a
  // depth-1-only history, so the `(+M)` segment never renders there.
  const descendantTools = useMemo(
    () =>
      tu.tool_use_id
        ? countSubtreeTools(tu.tool_use_id, childrenByParent) - toolCount
        : 0,
    [tu.tool_use_id, childrenByParent, toolCount],
  );

  const hasNestedSubagent = useMemo(
    () =>
      childMessages.some(
        (m) =>
          m.type === 'tool_use' && SUBAGENT_TOOL_NAMES.has(asToolUse(m).name),
      ),
    [childMessages],
  );

  // Depth badge only when the tree actually nests: any nested panel
  // (depth > 1) shows one, and the root shows N1 only when it has
  // subagent children. Keeps flat histories pixel-identical (spec §6.7).
  const showDepthBadge = depth > 1 || hasNestedSubagent;

  // Model this subagent is running as. Canonical source is the model
  // stamped on the subagent's own assistant_text children (persisted by
  // session-manager on every assistant event). Task input.model is only
  // set when the parent explicitly overrode the model, so it's a
  // fallback, not a primary source. If neither is present, we render no
  // model segment — never guess (santi-context: "Never fabricate").
  const model = useMemo<string | null>(() => {
    const firstText = childMessages.find((m) => m.type === 'assistant_text');
    if (firstText) {
      const parsed = asAssistantText(firstText);
      if (typeof parsed.model === 'string' && parsed.model.length > 0) {
        return parsed.model;
      }
    }
    if (typeof tu.input.model === 'string' && tu.input.model.length > 0) {
      return tu.input.model;
    }
    return null;
  }, [childMessages, tu.input.model]);

  const isError = tr?.is_error === true;
  // Edge case (spec §7): a nested subagent can still be running while an
  // ancestor already resolved — each level derives running purely from
  // its OWN missing tool_result, never from the ancestor's state.
  const isRunning = !tr;

  // Derive duration from the timestamps of the tool_use vs tool_result.
  const duration = useMemo(() => {
    if (!resultMessage) return null;
    const start = Date.parse(toolUseMessage.timestamp);
    const end = Date.parse(resultMessage.timestamp);
    if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
    return Math.max(0, end - start);
  }, [toolUseMessage, resultMessage]);

  // Each recursion level pairs its OWN immediate children — a nested
  // Task's tool_result is emitted inside the parent subagent's stream,
  // so it lives in this level's child list.
  const { resultByToolUseId, pairedResultIds } = useMemo(
    () => pairToolResults(childMessages),
    [childMessages],
  );

  return (
    <div
      className={`border rounded-lg overflow-hidden text-body ${
        isError
          ? 'border-status-error/40 bg-status-error/10'
          : 'border-border bg-popover/60'
      }`}
      data-testid="subagent-panel"
      data-subagent-type={subagentType}
      data-error={isError ? 'true' : 'false'}
      data-running={isRunning ? 'true' : 'false'}
      data-depth={depth}
    >
      <ChainOfThought defaultOpen={false}>
        <ChainOfThoughtHeader
          icon={BotIcon}
          data-testid="subagent-header"
          className={`px-3 py-2 hover:bg-accent focus:outline-none focus:ring-2 focus:ring-border ${
            isError ? 'hover:bg-status-error/15' : ''
          }`}
        >
          <span
            className={`flex min-w-0 items-center gap-2 text-body ${
              isError ? 'text-status-error' : 'text-foreground'
            }`}
          >
            {showDepthBadge && (
              <Badge
                variant="secondary"
                className="shrink-0 px-2 py-0.5 font-normal text-caption"
                data-testid="subagent-depth-badge"
              >
                {t('subagent.depthBadge', { depth })}
              </Badge>
            )}
            <span className="truncate">
              {t('subagent.label')} · {subagentType}
              {model !== null && ` · ${modelLabel(model)}`}
              {/* Sprint messages-section-fixes (D-msg-16) — only show the
                  tool count when there is at least one tool call. Pre-fix,
                  subagents that responded with text only rendered "0 tools",
                  which was confusing — a "tools" segment that says zero is
                  worse than no segment at all. */}
              {toolCount > 0 && (
                <>
                  {' · '}
                  {toolCount === 1
                    ? t('subagent.tools', { count: toolCount })
                    : t('subagent.toolsPlural', { count: toolCount })}
                  {descendantTools > 0 &&
                    ` ${t('subagent.descendantTools', { count: descendantTools })}`}
                </>
              )}
              {duration !== null && ` · ${(duration / 1000).toFixed(1)}s`}
              {isRunning && (
                <span className="ml-2 text-caption text-muted-foreground">
                  {t('subagent.running')}
                </span>
              )}
              {isError && (
                <span className="ml-2 text-caption text-status-error">
                  {t('subagent.error')}
                </span>
              )}
            </span>
          </span>
        </ChainOfThoughtHeader>
        <ChainOfThoughtContent
          className="mt-0 border-t border-border px-3 py-2 space-y-2"
          data-testid="subagent-body"
        >
          {/* Sprint messages-section-fixes (D-msg-12) — the raw prompt
              is developer-shaped (multi-line internal instructions to
              the subagent). Founder mode hides it; the subagent panel
              still expands and shows child events + a humanised final
              result so the founder gets a useful summary. */}
          {subagentPrompt && (
            <DeveloperOnly>
              <div>
                <div className="text-caption uppercase tracking-wide text-muted-foreground/70 mb-1">
                  {t('subagent.prompt')}
                </div>
                <pre className="bg-background border border-border rounded text-xs p-2 overflow-x-auto whitespace-pre-wrap break-words text-foreground"> {/* design-discipline: kept as text-xs — developer-mode subagent prompt */}
                  {subagentPrompt}
                </pre>
              </div>
            </DeveloperOnly>
          )}

          {childMessages.length > 0 && (
            <div className="space-y-2" data-testid="subagent-child-list">
              {childMessages.map((m) => {
                if (m.type === 'tool_result' && pairedResultIds.has(m.id)) {
                  return null;
                }
                let pairedResult: Message | undefined;
                let childTuId = '';
                let isNestedSubagent = false;
                if (m.type === 'tool_use') {
                  const childTu = asToolUse(m);
                  childTuId = childTu.tool_use_id;
                  if (childTuId) {
                    pairedResult = resultByToolUseId.get(childTuId);
                  }
                  isNestedSubagent =
                    SUBAGENT_TOOL_NAMES.has(childTu.name) && childTuId !== '';
                }
                return (
                  <div
                    key={m.id}
                    className="pl-3 border-l border-border"
                    data-message-id={m.id}
                  >
                    {isNestedSubagent && depth < MAX_RENDER_DEPTH ? (
                      <SubagentPanel
                        toolUseMessage={m}
                        childrenByParent={childrenByParent}
                        resultMessage={pairedResult}
                        depth={depth + 1}
                      />
                    ) : (
                      <MessageBubble
                        message={m}
                        toolResultMessage={pairedResult}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* D-msg-12 — the raw final-result `<pre>` is developer-only.
              The error case stays surfaced in the header (the
              `subagent.error` chip is rendered there); the founder
              already has the signal. The full text lives behind the
              toggle. */}
          {tr && (
            <DeveloperOnly>
              <div>
                <div
                  className={`text-caption uppercase tracking-wide mb-1 ${
                    isError ? 'text-status-error' : 'text-muted-foreground/70'
                  }`}
                >
                  {isError ? t('subagent.subagentError') : t('subagent.finalResult')}
                </div>
                <pre
                  className={`rounded text-xs p-2 overflow-x-auto whitespace-pre-wrap break-words bg-background border ${ /* design-discipline: kept as text-xs — developer-mode subagent result */
                    isError
                      ? 'border-status-error/40 text-status-error'
                      : 'border-border text-foreground'
                  }`}
                >
                  {toolResultText(tr.content) || (
                    <span className="text-muted-foreground/70">{t('subagent.emptyResult')}</span>
                  )}
                </pre>
              </div>
            </DeveloperOnly>
          )}
        </ChainOfThoughtContent>
      </ChainOfThought>
    </div>
  );
}
